import React, { useState, useEffect } from "react";
import { ThemeLoader } from "@/shared/components/ThemeLoader";
import "./OrdersModule.scss";
import { useOrders } from "@/shared/hooks/useOrders";
import { OrderCard } from "@/shared/components/OrderCard";
import { OrderDetails } from "@/shared/components/OrderDetails";
import { openPaymentUrl } from "@/shared/utils/openPaymentUrl";
import { resolveRuntimeMessenger } from "@/shared/utils/runtimeMessenger";
import { showAppAlert } from "@/shared/utils/appDialog";
import { useSearchParams } from "react-router-dom";

interface OrdersModuleProps {
  onClose: () => void;
}

export const OrdersModule: React.FC<OrdersModuleProps> = ({ onClose }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [payingOrderId, setPayingOrderId] = useState<string | null>(null);
  const {
    orders,
    loading,
    error,
    refreshOrders,
    fetchOrderById,
    updateOrderDelivery,
    updateOrderItems,
    addOrderItems,
    updateOrderPaymentMethod,
    cancelOrder,
    payOrder,
  } = useOrders();

  useEffect(() => {
    const orderIdFromUrl = searchParams.get("order_id")?.trim();
    if (!orderIdFromUrl) return;
    setSelectedOrderId(orderIdFromUrl);
    const next = new URLSearchParams(searchParams);
    next.delete("order_id");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handlePayFromList = async (orderId: string) => {
    setPayingOrderId(orderId);
    try {
      const result = await payOrder(orderId);
      const messengerKind = resolveRuntimeMessenger().kind;
      openPaymentUrl(result.paymentUrl);
      // В Telegram location.href обрывает последующие запросы — не дергаем refresh.
      if (messengerKind !== "telegram") {
        await refreshOrders();
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Не удалось запустить оплату";
      if (!/failed to fetch|networkerror|load failed/i.test(message)) {
        void showAppAlert(message);
      }
    } finally {
      setPayingOrderId(null);
    }
  };

  const handleDetailsClose = () => {
    setSelectedOrderId(null);
  };

  let body: React.ReactNode;
  if (loading) {
    body = <ThemeLoader />;
  } else if (error) {
    body = (
      <div className="orders-module__error">
        <p>{error}</p>
        <button type="button" onClick={() => void refreshOrders()}>
          Повторить
        </button>
      </div>
    );
  } else if (orders.length === 0) {
    body = (
      <div className="orders-module__empty">
        <p>У вас пока нет заказов</p>
        <button
          type="button"
          className="orders-module__catalog-btn"
          onClick={onClose}
        >
          Перейти в каталог
        </button>
      </div>
    );
  } else {
    body = (
      <div className="orders-module__list">
        {orders.map((order) => (
          <OrderCard
            key={order.id}
            order={order}
            onClick={() => setSelectedOrderId(order.id)}
            onPay={handlePayFromList}
            paying={payingOrderId === order.id}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="orders-module">
      {body}

      <OrderDetails
        orderId={selectedOrderId}
        onClose={handleDetailsClose}
        fetchOrderById={fetchOrderById}
        updateOrderDelivery={updateOrderDelivery}
        updateOrderItems={updateOrderItems}
        addOrderItems={addOrderItems}
        updateOrderPaymentMethod={updateOrderPaymentMethod}
        cancelOrder={cancelOrder}
        payOrder={payOrder}
      />
    </div>
  );
};
